// filepath: /Users/sjuul/workspace/robust-risk/frontend/src/components/APIKeyManagement.tsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Chip,
  SelectChangeEvent,
  FormControlLabel,
  Switch,
  InputAdornment,
  IconButton as MuiIconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import KeyIcon from '@mui/icons-material/Key';
import { useAppDispatch, useAppSelector } from '../store/hooks.ts';
import { selectAPIKeys, setAPIKeys, addAPIKey, updateAPIKey as updateAPIKeyAction, removeAPIKey } from '../store/apiKey/apiKeySlice.ts';
import { selectCurrentOrganization } from '../store/organization/organizationSlice.ts';
import {
  listAPIKeys,
  createAPIKey,
  updateAPIKey,
  deleteAPIKey,
} from '../api.ts';
import { APIKey, APIKeyCreate, APIKeyUpdate, APIKeyVendor, VENDOR_NAMES } from '../models/apiKey.ts';

const VENDORS: APIKeyVendor[] = ['openai', 'anthropic', 'google', 'cohere', 'groq'];

const APIKeyManagement = () => {
  const dispatch = useAppDispatch();
  const apiKeys = useAppSelector(selectAPIKeys);
  const currentOrganization = useAppSelector(selectCurrentOrganization);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Add key dialog state
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newKeyVendor, setNewKeyVendor] = useState<APIKeyVendor>('openai');
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyValue, setNewKeyValue] = useState('');
  const [newKeyIsDefault, setNewKeyIsDefault] = useState(false);
  const [showNewKeyValue, setShowNewKeyValue] = useState(false);

  // Edit dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingKey, setEditingKey] = useState<APIKey | null>(null);
  const [editName, setEditName] = useState('');
  const [editIsDefault, setEditIsDefault] = useState(false);

  // Delete confirmation dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingKey, setDeletingKey] = useState<APIKey | null>(null);

  const loadAPIKeys = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await listAPIKeys();
      dispatch(setAPIKeys(data));
    } catch (err: any) {
      console.error('Failed to load API keys:', err);
      setError(err.response?.data?.detail || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    if (currentOrganization) {
      loadAPIKeys();
    }
  }, [currentOrganization, loadAPIKeys]);

  const handleAddKey = async () => {
    if (!newKeyName.trim() || !newKeyValue.trim()) return;

    try {
      setLoading(true);
      setError(null);

      // Check if this is the first key for this vendor
      const existingKeysForVendor = apiKeys.filter(key => key.vendor === newKeyVendor);
      const shouldBeDefault = existingKeysForVendor.length === 0 || newKeyIsDefault;

      const keyData: APIKeyCreate = {
        vendor: newKeyVendor,
        name: newKeyName,
        api_key: newKeyValue,
        is_default: shouldBeDefault,
      };
      const response = await createAPIKey(keyData);
      dispatch(addAPIKey(response));
      setSuccess('API key added successfully');
      setShowAddDialog(false);
      resetAddForm();
      await loadAPIKeys();
    } catch (err: any) {
      console.error('Failed to add API key:', err);
      setError(err.response?.data?.detail || 'Failed to add API key');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateKey = async () => {
    if (!editingKey || !editingKey.id) return;

    try {
      setLoading(true);
      setError(null);
      const updateData: APIKeyUpdate = {
        name: editName,
        is_default: editIsDefault,
      };
      const response = await updateAPIKey(editingKey.id, updateData);
      dispatch(updateAPIKeyAction(response));
      setSuccess('API key updated successfully');
      setShowEditDialog(false);
      setEditingKey(null);
      await loadAPIKeys();
    } catch (err: any) {
      console.error('Failed to update API key:', err);
      setError(err.response?.data?.detail || 'Failed to update API key');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteKey = async () => {
    if (!deletingKey || !deletingKey.id) return;

    try {
      setLoading(true);
      setError(null);
      await deleteAPIKey(deletingKey.id);
      dispatch(removeAPIKey(deletingKey.id));
      setSuccess('API key deleted successfully');
      setShowDeleteDialog(false);
      setDeletingKey(null);
    } catch (err: any) {
      console.error('Failed to delete API key:', err);
      setError(err.response?.data?.detail || 'Failed to delete API key');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (key: APIKey) => {
    setEditingKey(key);
    setEditName(key.name);
    setEditIsDefault(key.is_default);
    setShowEditDialog(true);
  };

  const openDeleteDialog = (key: APIKey) => {
    setDeletingKey(key);
    setShowDeleteDialog(true);
  };

  const resetAddForm = () => {
    setNewKeyVendor('openai');
    setNewKeyName('');
    setNewKeyValue('');
    setNewKeyIsDefault(false);
    setShowNewKeyValue(false);
  };

  const getVendorColor = (vendor: string) => {
    switch (vendor) {
      case 'openai': return 'success';
      case 'anthropic': return 'warning';
      case 'google': return 'info';
      case 'cohere': return 'secondary';
      case 'groq': return 'primary';
      default: return 'default';
    }
  };

  const isAdmin = currentOrganization?.role === 'admin' || currentOrganization?.role === 'owner';

  if (!currentOrganization) {
    return (
      <Alert severity="warning">
        Please select an organization to manage API keys.
      </Alert>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          API Keys
        </Typography>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowAddDialog(true)}
            disabled={loading}
          >
            Add API Key
          </Button>
        )}
      </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {!isAdmin && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You need admin or owner role to manage API keys.
            </Alert>
          )}

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Vendor</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Key</TableCell>
                  <TableCell>Default</TableCell>
                  <TableCell>Created</TableCell>
                  {isAdmin && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && apiKeys.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 6 : 5} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : apiKeys.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 6 : 5} align="center">
                      No API keys found. Add one to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  apiKeys.map((key: APIKey) => (
                    <TableRow key={key.id}>
                      <TableCell>
                        <Chip
                          label={VENDOR_NAMES[key.vendor as APIKeyVendor]}
                          color={getVendorColor(key.vendor)}
                          size="small"
                          icon={<KeyIcon />}
                        />
                      </TableCell>
                      <TableCell>{key.name}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {key.masked_key}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {key.is_default ? (
                          <Chip label="Default" size="small" color="primary" />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {key.created_at ? new Date(key.created_at).toLocaleDateString() : '-'}
                        </Typography>
                      </TableCell>
                      {isAdmin && (
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => openEditDialog(key)}
                            disabled={loading}
                            color="primary"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => openDeleteDialog(key)}
                            disabled={loading}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

      {/* Add Key Dialog */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add API Key</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Your API key will be encrypted and stored securely. Only one default key per vendor is allowed per organization.
          </Alert>

          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Vendor</InputLabel>
            <Select
              value={newKeyVendor}
              label="Vendor"
              onChange={(e: SelectChangeEvent) => setNewKeyVendor(e.target.value as APIKeyVendor)}
              disabled={loading}
            >
              {VENDORS.map((vendor) => (
                <MenuItem
                  key={vendor}
                  value={vendor}
                  disabled={vendor !== 'openai' && vendor !== 'groq'}
                  sx={vendor !== 'openai' && vendor !== 'groq' ? { color: 'text.disabled' } : {}}
                >
                  {VENDOR_NAMES[vendor]}
                  {vendor !== 'openai' && vendor !== 'groq' && ' (Coming Soon)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            margin="dense"
            label="Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newKeyName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewKeyName(e.target.value)}
            disabled={loading}
            placeholder="e.g., Production Key"
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            label="API Key"
            type={showNewKeyValue ? 'text' : 'password'}
            fullWidth
            variant="outlined"
            value={newKeyValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewKeyValue(e.target.value)}
            disabled={loading}
            placeholder="sk-..."
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <MuiIconButton
                    onClick={() => setShowNewKeyValue(!showNewKeyValue)}
                    edge="end"
                  >
                    {showNewKeyValue ? <VisibilityOff /> : <Visibility />}
                  </MuiIconButton>
                </InputAdornment>
              ),
            }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={newKeyIsDefault || apiKeys.filter(key => key.vendor === newKeyVendor).length === 0}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewKeyIsDefault(e.target.checked)}
                disabled={loading || apiKeys.filter(key => key.vendor === newKeyVendor).length === 0}
              />
            }
            label={
              apiKeys.filter(key => key.vendor === newKeyVendor).length === 0
                ? "Will be set as default (first key for this vendor)"
                : "Set as default for this vendor"
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowAddDialog(false); resetAddForm(); }} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleAddKey}
            variant="contained"
            disabled={loading || !newKeyName.trim() || !newKeyValue.trim()}
          >
            Add Key
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Key Dialog */}
      <Dialog open={showEditDialog} onClose={() => setShowEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit API Key</DialogTitle>
        <DialogContent>
          {editingKey && (
            <>
              <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
                You can only update the name and default status. The key value cannot be changed.
              </Alert>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Vendor: <Typography component="span" fontWeight="bold">{VENDOR_NAMES[editingKey.vendor as APIKeyVendor]}</Typography>
              </Typography>

              <TextField
                margin="dense"
                label="Name"
                type="text"
                fullWidth
                variant="outlined"
                value={editName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditName(e.target.value)}
                disabled={loading}
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={editIsDefault}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditIsDefault(e.target.checked)}
                    disabled={loading}
                  />
                }
                label="Set as default for this vendor"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditDialog(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateKey}
            variant="contained"
            disabled={loading || !editName.trim()}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete API Key</DialogTitle>
        <DialogContent>
          {deletingKey && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This action cannot be undone. Any services using this key will stop working.
              </Alert>
              <Typography>
                Are you sure you want to delete the API key <Typography component="span" fontWeight="bold">{deletingKey.name}</Typography> for {VENDOR_NAMES[deletingKey.vendor as APIKeyVendor]}?
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteKey}
            variant="contained"
            color="error"
            disabled={loading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default APIKeyManagement;

